import {
  Button, Checkbox,
  Form,
  FormListFieldData,
  FormListOperation,
  Input,
  InputNumber,
  message,
  Modal,
  Result, Select,
  Space,
  Spin
} from "antd";
import { useContext, useEffect, useState } from "react";
import { ConfiguratorContext, PricingSnapshotListContext, PricingSnapshotsContext } from "../context";
import dayjs from 'dayjs'
import {useAsyncState} from "../hook/useAsyncState";
import {PricingConfig, TariffCondition, SupplementalCharge} from "../api/models";
import {useForm} from "antd/lib/form/Form";
import Utils from "../util/util";
import useUsers from "../swr/useUsers";
import {DeleteOutlined, PlusOutlined} from "@ant-design/icons";
import useCategory from "../swr/useCategory";
import CustomerWarrantyManagement from "./CustomerWarrantyManagement";
import { ChargeTargetingButton } from "./SupplementalChargeTargeting";

const DATE_DISPLAY_FORMAT = "MMM Do YYYY, H:mm";
const trimZero = (val:string|undefined) => {
  const rslt = val?.replace(/^0+/, '').replace(/\.*0+%$/, '%') || '';
  return rslt === '%' ? '0%' : rslt;
}

const moneyInputFormatter = (value:string|undefined) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
const moneyInputParser = (value:string|undefined) => value!.replace(/\$\s?|(,*)/g, '');

const percentInputFormatter = (value:string|undefined) => trimZero( ((parseFloat(String(value)) || 0.0) * 100).toFixed(4)+"%" )
const percentInputParser = (value:string|undefined) => String( parseFloat( value!.replace(/\%\s?|(,*)/g, '') ) / 100 ) 

const validateRange = (min: number, max: number) => {
  return async (_rule: any, value: string) => {
    const n = Number(value);
    if (n >= min && n <= max) {
      return;
    } else {
      throw new Error("invalid");
    }
  };
};

/*************************************************
 *************************************************/

const SaveReminderModal = (props:{
  saveCnt:number 
}) => {
  const {saveCnt} = props;
  const [dismissCnt, setDismissCnt] = useState<number>(0)

  const SHOW_SAVE_REMINDER = 1;

  useEffect(()=> {
    if ( saveCnt > SHOW_SAVE_REMINDER  ) {
      message.success("Successfully updated.");
    }
  }, [ saveCnt ] );

  const handleDismissReminder = () => {
    setDismissCnt( saveCnt );
  }

  const showModal = saveCnt == SHOW_SAVE_REMINDER && saveCnt != dismissCnt;
  return <Modal 
    open={showModal}
    onOk={handleDismissReminder}
    cancelButtonProps={{ style: { display: 'none' } }} 
  >
    <Result title="Changes are not active."
      subTitle="Pricing configuration changes must be activated before they will affect quotes."
    />
  </Modal>
}

/*************************************************
 *************************************************/
const ConfirmActivateModal = (props: {
  showConfirmActivate:boolean
  loading:boolean
  handleConfirm:(name:string, description:string)=>void
  handleCancel:()=>void
}) => {
  const {loading, showConfirmActivate, handleCancel, handleConfirm} = props;
  const [form] = useForm();

  const handleFinish =  (values:Record<string,string>) => {
    handleConfirm( values.name, values.description );
  }

  return <Form
    name="confirmActivate"
    initialValues={{ name: "", description: "" }}
    form={form}
    onFinish={handleFinish}
  >
    <Modal
      title="Name the configuration"
      open={showConfirmActivate}
      onCancel={handleCancel}
      footer={[
        (<Button key="2" onClick={handleCancel}>Cancel</Button>),
        (<Button key="3" type="primary" loading={loading} onClick={()=>form.submit()} >Save</Button>)
      ]}
    >
      <div style={{marginBottom: '16px'}}>
        <div style={{marginBottom: '4px', fontWeight: '500'}}>Public Name <span style={{color: 'red'}}>*</span></div>
        <div style={{fontSize: '0.9em', color: '#666', marginBottom: '8px'}}>
          This name will be visible to all Configurator users, including dealers.
        </div>
        <Form.Item
          name="name"
          rules={[{ required: true, message: 'Public Name is required' }]}
          style={{marginBottom: 0}}
        >
          <Input
            placeholder="e.g., 'Q1 2025 Pricing' or 'November Update'"
          />
        </Form.Item>
      </div>

      <div>
        <div style={{marginBottom: '4px', fontWeight: '500'}}>Description</div>
        <div style={{fontSize: '0.9em', color: '#666', marginBottom: '8px'}}>
          Internal notes - not visible to dealers or external users.
        </div>
        <Form.Item
          name="description"
          style={{marginBottom: 0}}
        >
          <Input.TextArea
            rows={4}
            placeholder="Describe what pricing changes were made (e.g., 'Updated labor costs and added new warranty tiers')"
          />
        </Form.Item>
      </div>
    </Modal>
  </Form>
}
/*************************************************
 *************************************************/
const PricingConfigForm = (props:{
  loading:boolean
  onFinish:(values:Record<string,string>)=>void
  pricingConfig:PricingConfig | undefined
  handleShowConfirmActivate: ()=>void
  handleShowCustomerWarrantyModal: ()=>void
}) => {

  const userLst = useUsers().data;
  const {loading, onFinish, pricingConfig, handleShowConfirmActivate, handleShowCustomerWarrantyModal } = props;
  const [isFormDirty, setFormDirty] = useState<boolean>(false)
  const [form] = useForm();

  useEffect(()=> {
    resetForm();
  }, [ pricingConfig ] );

  const handleValuesChange = (_changes:Record<string,string>, allValues:Record<string,string>) => {
    const diff = Utils.diff( allValues, pricingConfig );
    if( diff.length ) {

      setFormDirty( true );
      return;
    }
    setFormDirty(false );
  }

  const resetForm = () => {
    form.resetFields();
    setFormDirty( false );
  }
  const handleReset = () => {
    resetForm();
  };

  const createdBy = userLst?.find( u => u.id === pricingConfig?.createdBy )?.name || pricingConfig?.createdBy;

  return <>
    <Spin spinning={loading}>

      <Space direction="vertical" size="middle" >
        <Form
          name="pricingConfig"
          form={form}
          onFinish={onFinish}
          autoComplete="off"
          labelCol={{flex: "200px"}}
          labelWrap={true}
          labelAlign="right"
          onValuesChange={handleValuesChange}
          initialValues={pricingConfig}
        >

          <div style={{display: "flex", justifyContent: "space-between", gap:"12px", marginBottom: "20px"}}>
            <div style={{flex:"1 2", paddingRight: "40px" }}>
              <span style={{marginRight:"4px", fontWeight: "600"}}>Updated By:</span>
              <span style={{marginRight:"4px"}}>{createdBy}</span>
              on {dayjs(pricingConfig?.createdAt).format("MMMM Do YYYY, h:mm:ss a")}
            </div>
            <Button onClick={handleShowCustomerWarrantyModal}>
              Manage Customer Warranties
            </Button>
            <Button onClick={handleShowConfirmActivate} disabled={isFormDirty}>
              Activate
            </Button>
            <Button onClick={handleReset} disabled={!isFormDirty}>
              Reset
            </Button>
            <Button type="primary" onClick={()=>form.submit()}>
              Save
            </Button>
          </div>


          <Form.Item
            label="Labor Hourly Cost (Dollars)"
            name="laborHourlyCost"
            rules={[
              { required: true, message: "Labor Hourly Cost is required" },
              {
                type: "number",
                validator: validateRange(0, 1000),
                message: "Must be between 0 and 1,000.00",
              },
            ]}
          >
            <InputNumber
              formatter={moneyInputFormatter}
              parser={moneyInputParser}
              placeholder="Type dollar amount"
              controls={false}
              style={{width:"100%"}}
            />
          </Form.Item>

          <Form.Item
            label="PDI"
            name="pdi"
            rules={[
              { required: true, message: "PDI is required" },
              {
                type: "number",
                validator: validateRange(0, 10000),
                message: "Must be between 0 and 10,000.00",
              },
            ]}
          >
            <InputNumber
              formatter={moneyInputFormatter}
              parser={moneyInputParser}
              placeholder="Type dollar amount"
              controls={false}
              style={{width:"100%"}}
            />
          </Form.Item>

          <Form.Item
            label="Warranty (fixed cost)"
            name="warranty"
            rules={[
              { required: true, message: "Warranty is required" },
              {
                type: "number",
                validator: validateRange(0, 10000),
                message: "Must be between 0 and 10,000.00",
              },
            ]}
          >
            <InputNumber
              formatter={moneyInputFormatter}
              parser={moneyInputParser}
              placeholder="Type dollar amount"
              controls={false}
              style={{width:"100%"}}
            />
          </Form.Item>

          <Form.Item
            label="Warranty (Percentage of Material Cost)"
            name="warrantyPercentage"
            rules={[
              { required: true, message: "Warranty (Percentage) is required" },
              {
                type: "number",
                validator: validateRange(0, 1.0),
                message: "Must be between 0 and 1.0",
              },
            ]}
          >
            <InputNumber
              formatter={percentInputFormatter}
              parser={percentInputParser}
              placeholder="Type percent" 
              controls={false}
              style={{width:"100%"}}
            />
          </Form.Item>
          <Form.Item

            label="BEV Warranty (Percentage of Material Cost)"
            name="bevWarrantyPercentage"
            rules={[
              { required: true, message: "BEV Warranty (Percentage) is required" },
              {
                type: "number",
                validator: validateRange(0, 1.0),
                message: "Must be between 0 and 1.0",
              },
            ]}
          >
            <InputNumber
              formatter={percentInputFormatter}
              parser={percentInputParser}
              placeholder="Type percent" 
              controls={false}
              style={{width:"100%"}}
            />
          </Form.Item>

          <Form.Item

            label="Extended Warranty Markup"
            name="extendedWarrantyMarkup"
            rules={[
              { required: true, message: "Extended Warranty Markup (Percentage)" },
              {
                type: "number",
                validator: validateRange(0, 99999.0),
                message: "Must be between 0 and 99999.0",
              },
            ]}
          >
            <InputNumber
              formatter={percentInputFormatter}
              parser={percentInputParser}
              placeholder="Type percent" 
              controls={false}
              style={{width:"100%"}}
            />
          </Form.Item>

          <Form.Item
            label="Tariffs and Surcharges"
            name="tariffsSurcharges"
            rules={[
              {
                required: true,
                message: "Tariffs and Surcharges is required",
              },
              {
                type: "number",
                validator: validateRange(0, 10000),
                message: "Must be between 0 and 10,000.00",
              },
            ]}
          >
            <InputNumber
              formatter={moneyInputFormatter}
              parser={moneyInputParser}
              placeholder="Type dollar amount"
              controls={false}
              style={{width:"100%"}}
            />
          </Form.Item>

          <Form.Item
            label="Margin"
            name="margin"
            rules={[
              { required: true, message: "Margin is required" },
              {
                type: "number",
                validator: validateRange(0, 0.999999),
                message: "Must be between 0 and 1.0",
              },
            ]}
          >
            <InputNumber
              formatter={percentInputFormatter}
              parser={percentInputParser}
              placeholder="Type percent" 
              controls={false}
              style={{width:"100%"}}
            />
          </Form.Item>

          <Form.Item
            label="Dealer Markup"
            name="dealerMarkup"
            rules={[
              { required: true, message: "Dealer Markup is required" },
              {
                type: "number",
                validator: validateRange(0, 0.999999),
                message: "Must be between 0 and 1.0",
              },
            ]}
          >
            <InputNumber
              formatter={percentInputFormatter}
              parser={percentInputParser}
              placeholder="Type percent" 
              controls={false}
              style={{width:"100%"}}
            />
          </Form.Item>

          <Form.Item
            label="Inbound Freight"
            name="inboundFreight"
            rules={[
              { required: true, message: "Inbound Freight is required" },
              {
                type: "number",
                validator: validateRange(0, 1.0),
                message: "Must be between 0 and 1.0",
              },
            ]}
          >
            <InputNumber
              formatter={percentInputFormatter}
              parser={percentInputParser}
              placeholder="Type percent" 
              controls={false}
              style={{width:"100%"}}
            />
          </Form.Item>

          <Form.List name="tariffConditions" >
            {(fields, operations, _meta) => <>
              <div style={{display:"flex", gap: ".5rem", marginBottom: ".5rem"}}>Tariff Conditions <Button icon={<PlusOutlined/>} type="primary" shape="circle" size="small" onClick={() => operations.add()} /></div>
              {fields?.map((fld, ndx) => <TariffConditionForm key={"tariffCondition-" + ndx} fld={fld} ndx={ndx} operations={operations} selected={form.getFieldValue("tariffConditions")} /> )}
            </>}
          </Form.List>

          <Form.List name="supplementalCharges" >
            {(fields, operations, _meta) => <>
              <div style={{display:"flex", gap: ".5rem", marginBottom: ".5rem"}}>Supplemental Charges <Button icon={<PlusOutlined/>} type="primary" shape="circle" size="small" onClick={() => operations.add()} /></div>
              {fields?.map((fld, ndx) => <SupplementalChargeForm key={"supplementalCharge-" + ndx} fld={fld} ndx={ndx} operations={operations} /> )}
            </>}
          </Form.List>

        </Form>

      </Space>
    </Spin>
  </>
}

const EngineModelCategoryId = "engine_model";
const EngineFamilyName = "EngineFamily";
const TariffConditionForm = (props: {
  key: React.Key
  fld: FormListFieldData
  ndx: number
  operations: FormListOperation
  selected: TariffCondition[]
}) => {
  const {fld, ndx, operations} = props;

  const selected = props.selected.filter(v=>v).map( tc => tc.engineFamily)

  const engineModelCategoryAsync = useCategory({ categoryId: EngineModelCategoryId });
  const engineFamilyLst = engineModelCategoryAsync.data?.metadata
      .filter( cm => cm.name === EngineFamilyName)
      .flatMap( cm => cm.selectOptions as string[] )
      .filter( o => !selected.includes(o) );

  return <div style={{display: "flex", alignItems:"baseline", gap: "1rem"}}>
    <Form.Item
        name={[fld.name, "id"]}
        hidden={true}
    >
      <Input />
    </Form.Item>
    <div style={{width: "10rem"}}>
    <Form.Item
        name={[fld.name, "engineFamily"]}
        rules={[{
          required: true,
          message: `An engine family is required.`,
        }]}
    >
      <Select
          loading={engineModelCategoryAsync.isLoading}
          showSearch
          allowClear
          options={engineFamilyLst?.map(f => ({ label: f, value: f}))}
          placeholder={"Engine Family"}
      />
    </Form.Item>
    </div>
    <Form.Item
        name={[fld.name, "value"]}
        rules={[{
          required: true,
          message: `A value is required.`,
        }]}
    >
      <InputNumber
          formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={value => value!.replace(/\$\s?|(,*)/g, '')}
          style={{width: "100%"}}
          controls={false}
      />
    </Form.Item>
    <Form.Item
        name={[fld.name, "disabled"]}
        valuePropName="checked"
        label="Disabled"
    >
      <Checkbox />
    </Form.Item>
    <div style={{marginLeft: "2rem"}}>
    <Button icon={<DeleteOutlined/>} type="text" danger shape="circle" size="small" onClick={() => operations.remove(ndx)} />
    </div>
  </div>;

}

const SupplementalChargeForm = (props: {
  key: React.Key
  fld: FormListFieldData
  ndx: number
  operations: FormListOperation
}) => {
  const {fld, ndx, operations} = props;

  return <div style={{display: "flex", alignItems:"baseline", gap: "1rem"}}>
    <Form.Item
        name={[fld.name, "id"]}
        hidden={true}
    >
      <Input />
    </Form.Item>
    <div style={{width: "15rem"}}>
    <Form.Item
        name={[fld.name, "label"]}
        rules={[{
          required: true,
          message: `A label is required.`,
        }]}
    >
      <Input placeholder="Label" />
    </Form.Item>
    </div>
    <Form.Item
        name={[fld.name, "value"]}
        rules={[{
          required: true,
          message: `A value is required.`,
        }]}
    >
      <InputNumber
          formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={value => value!.replace(/\$\s?|(,*)/g, '')}
          style={{width: "100%"}}
          controls={false}
      />
    </Form.Item>
    <Form.Item name={[fld.name, "conditionDefinition"]}>
      <ChargeTargetingButton />
    </Form.Item>
    <div style={{marginLeft: "2rem"}}>
    <Button icon={<DeleteOutlined/>} type="text" danger shape="circle" size="small" onClick={() => operations.remove(ndx)} />
    </div>
  </div>;

}

/*************************************************
 *************************************************/
const PricingConfigView = () => {
  const configurator = useContext(ConfiguratorContext);
  const { loadPricingSnapshotList } = useContext<PricingSnapshotListContext>(PricingSnapshotsContext);

  const [pricingConfig, pricingConfigAsync ] = useAsyncState<PricingConfig>();
  const [showConfirmActivate, setShowConfirmActivate] = useState<boolean>(false)
  const [saveCnt, setSaveCnt] = useState<number>(0)
  const [showCustomerWarrantyModal, setShowCustomerWarrantyModal] = useState<boolean>(false)

  //show failure message
  useEffect(()=> {
    if ( pricingConfigAsync.isFail() )  {
      message.error("Pricing list failed to load. " + pricingConfigAsync.err );
    }
    if ( pricingConfigAsync.isInitial() )  {
      loadActivePricing();
    }
  }, [ pricingConfigAsync.state ] );

  const loadActivePricing = () => {
    pricingConfigAsync.setLoading();
    configurator.api.getActivePricingConfig()
    .then(resp => {
      pricingConfigAsync.setDone(resp.data)},
      reason => pricingConfigAsync.setFail(reason));
  }

  const handleSavePricingConfig = async (values: any) => {

    pricingConfigAsync.setLoading();
    configurator.api.updatePricing(values)
    .then(resp => {
      pricingConfigAsync.setDone(resp.data)

      setSaveCnt( saveCnt + 1 );
    },
    reason => pricingConfigAsync.setFail(reason));

  }

  const handleActivatePricingConfig = (name:string, description:string) => {
    setShowConfirmActivate( !showConfirmActivate );

    pricingConfigAsync.setLoading();
    configurator.api.createSnapshot( name, description )
    .then(resp => {
      pricingConfigAsync.setDone(resp.data)

      //reload pricing snapshots
      loadPricingSnapshotList();

      message.success("The pricing configuration is now active.");
    },
    reason => pricingConfigAsync.setFail(reason));
  }

  const handleShowConfirmActivate = () => {
    setShowConfirmActivate( !showConfirmActivate );
  }


  return <>
    <SaveReminderModal saveCnt={saveCnt} />

    <ConfirmActivateModal
      loading={pricingConfigAsync.isLoading()}
      showConfirmActivate={showConfirmActivate}
      handleConfirm={handleActivatePricingConfig}
      handleCancel={handleShowConfirmActivate}
    />

    <CustomerWarrantyManagement
      open={showCustomerWarrantyModal}
      onClose={() => setShowCustomerWarrantyModal(false)}
    />

    <PricingConfigForm
      loading={pricingConfigAsync.isLoading()}
      onFinish={handleSavePricingConfig}
      pricingConfig={pricingConfig}
      handleShowConfirmActivate={handleShowConfirmActivate}
      handleShowCustomerWarrantyModal={() => setShowCustomerWarrantyModal(true)}
    />
  </>
}
export default PricingConfigView;
